import type { Asset, CsvResponse, IUnderlyingUniverse } from 'apiConnector/underlying-universe';
import { underlyingUniverseConnector } from 'apiConnector/underlying-universe';
import { LightAlert } from 'components/shared/LightAlert';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import type { Observable } from 'rxjs';
import { getIsUserInternal } from 'state/selectors';
import type { AppState } from 'state/state.model';
import { httpClient } from 'utils/http/httpClient';

import { UnderlyingUniversePresentation } from './UnderlyingUniversePresentation';
import { UnderlyingUniverseSkeleton } from './UnderlyingUniverseSkeleton';

type UnderlyingUniverseStatus = 'loading' | 'error' | 'loaded';

interface Props {
  fetchData(): Observable<IUnderlyingUniverse>;
  fetchCSV(asset: Asset, query?: string): Observable<CsvResponse>;
}

export interface StateProps {
  isInternal?: boolean;
}

export type UnderlyingUniverseProps = Props & StateProps;

export const UnderlyingUniverseRaw: FC<UnderlyingUniverseProps> = ({ fetchData, fetchCSV, isInternal }) => {
  const { formatMessage } = useIntl();

  const [data, setData] = useState<IUnderlyingUniverse>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const [fetchDataRetries, setFetchDataRetries] = useState<number>(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to rerun this effect every time fetchDataRetries is updated
  useEffect(() => {
    const subscription = fetchData().subscribe({
      next(data) {
        setIsLoading(false);
        setIsError(false);
        setData(data);
      },
      error() {
        setIsError(true);
        setIsLoading(false);
      },
    });
    return function cleanup() {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    };
  }, [fetchData, fetchDataRetries]);

  const handleRetry = useCallback(() => {
    setIsLoading(true);
    setFetchDataRetries(fetchDataRetries + 1);
  }, [fetchDataRetries]);

  const status = useMemo<UnderlyingUniverseStatus>(() => {
    if (isLoading) {
      return 'loading';
    }
    if (isError) {
      return 'error';
    } else {
      return 'loaded';
    }
  }, [isLoading, isError]);

  return (
    <>
      {status === 'loading' && <UnderlyingUniverseSkeleton />}
      {status === 'loaded' && (
        <UnderlyingUniversePresentation data={data!} isInternal={isInternal} fetchCSV={fetchCSV} />
      )}
      {status === 'error' && (
        <div className="w-100 mt-2">
          <LightAlert severity="warning" e2e="underlying-universe-error">
            <span className="flex-grow-1">
              {formatMessage({
                id: 'discover.fetchData.error',
                defaultMessage: 'Technical error while fetching data',
              })}
            </span>
            <button className="btn btn-sm btn-outline-warning float-end" onClick={handleRetry}>
              {formatMessage({
                id: 'discover.fetchData.retry',
                defaultMessage: 'retry',
              })}
            </button>
          </LightAlert>
        </div>
      )}
    </>
  );
};

UnderlyingUniverseRaw.displayName = 'UnderlyingUniverse';

export const mapStateToProps = (state: AppState): StateProps => {
  const isInternal = getIsUserInternal(state);

  return {
    isInternal,
  };
};

export const connectComponent = connect(mapStateToProps);

const ConnectedUnderlyingUniverse = connectComponent(UnderlyingUniverseRaw);

export const underlyingUniverseApi = underlyingUniverseConnector(httpClient);
export const UnderlyingUniverse: FC = () => (
  <ConnectedUnderlyingUniverse fetchData={underlyingUniverseApi.fetchAll} fetchCSV={underlyingUniverseApi.fetchCSV} />
);
